/**
 * device/apps/contacts.ts — Contacts & Relationships
 *
 * Shows everyone the player has met in-world, sorted by bond strength.
 * Each contact is a card with an avatar circle, name, online status,
 * bond meter, tier label, and last-seen location. Bond grows through
 * interaction — the warmer the bar, the closer the friendship.
 *
 * All rendering is pure Canvas 2D — zero DOM.
 */

import type { GameState } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import {
  registerApp,
  drawRoundRect,
  drawText,
  drawProgressBar,
} from "../app.js";

// ─── Types ───────────────────────────────────────────────────────────────

interface Contact {
  name: string;
  bond: number;        // 0–100
  online: boolean;
  lastSeen: string;    // location name
  avatarColor: string; // hex color for the initial circle
}

interface ContactsState {
  scrollY: number;
  scrollVelocity: number;
  maxScrollY: number;
  selectedIndex: number; // -1 = none
}

// ─── Sample Contacts ─────────────────────────────────────────────────────

const SAMPLE_CONTACTS: Contact[] = [
  { name: "Lumi",       bond: 92,  online: true,  lastSeen: "Rooftop Garden",   avatarColor: "#8b5cf6" },
  { name: "Kai",        bond: 74,  online: true,  lastSeen: "Arcade",           avatarColor: "#3b82f6" },
  { name: "Fern",       bond: 61,  online: false, lastSeen: "Meadow Cottage",   avatarColor: "#22c55e" },
  { name: "Milo",       bond: 45,  online: true,  lastSeen: "Town Square",      avatarColor: "#f59e0b" },
  { name: "Dove",       bond: 33,  online: false, lastSeen: "Library",          avatarColor: "#ec4899" },
  { name: "Sage",       bond: 21,  online: false, lastSeen: "Riverside Market", avatarColor: "#06b6d4" },
  { name: "Rex",        bond: 12,  online: false, lastSeen: "Harbor Docks",     avatarColor: "#ef4444" },
  { name: "Opal",       bond: 5,   online: true,  lastSeen: "Crystal Cavern",   avatarColor: "#a78bfa" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────

function bondTier(bond: number): string {
  if (bond >= 80) return "Best Friend";
  if (bond >= 60) return "Close Friend";
  if (bond >= 40) return "Friend";
  if (bond >= 20) return "Acquaintance";
  return "Stranger";
}

/** Pick a warm-gradient color based on bond level (gray -> amber -> pink -> red) */
function bondColor(bond: number): string {
  if (bond >= 80) return "#ef4444"; // red — best friend
  if (bond >= 60) return "#ec4899"; // pink — close friend
  if (bond >= 40) return "#f59e0b"; // amber — friend
  if (bond >= 20) return "#d97706"; // dark amber — acquaintance
  return "#6b7280";                 // gray — stranger
}

/** Tier label color — slightly muted version of bond color */
function tierColor(bond: number): string {
  if (bond >= 80) return "rgba(239,68,68,0.8)";
  if (bond >= 60) return "rgba(236,72,153,0.7)";
  if (bond >= 40) return "rgba(245,158,11,0.7)";
  if (bond >= 20) return "rgba(217,119,6,0.6)";
  return "rgba(107,114,128,0.6)";
}

function getContacts(): Contact[] {
  // Sorted by bond descending (closest friends first)
  return [...SAMPLE_CONTACTS].sort((a, b) => b.bond - a.bond);
}

function getState(state: GameState): ContactsState {
  if (!state.player.device.appStates["contacts"]) {
    state.player.device.appStates["contacts"] = {
      scrollY: 0,
      scrollVelocity: 0,
      maxScrollY: 0,
      selectedIndex: -1,
    } satisfies ContactsState;
  }
  return state.player.device.appStates["contacts"] as ContactsState;
}

// ─── Layout Constants ────────────────────────────────────────────────────

const CARD_HEIGHT = 52;
const CARD_GAP = 4;
const CARD_RADIUS = 8;
const CARD_PAD_X = 8;
const CARD_BG = "rgba(28,28,30,0.85)";
const CARD_BG_SELECTED = "rgba(48,48,52,0.9)";
const HEADER_HEIGHT = 22;

const AVATAR_RADIUS = 14;
const STATUS_DOT_RADIUS = 3;

const NAME_FONT = "bold 9px sans-serif";
const TIER_FONT = "7px sans-serif";
const LOCATION_FONT = "7px sans-serif";
const BOND_FONT = "bold 6px monospace";
const HEADER_FONT = "bold 8px sans-serif";
const COUNT_FONT = "8px sans-serif";

// ─── Contacts App ────────────────────────────────────────────────────────

const contactsApp: DeviceApp = {
  id: "contacts",
  name: "Contacts",
  iconGradient: ["#6366f1", "#4f46e5"],

  drawIcon(ctx, cx, cy, size) {
    // Person silhouette: circle head + curved shoulder arc
    ctx.fillStyle = "#ffffff";

    // Head
    const headR = size * 0.15;
    ctx.beginPath();
    ctx.arc(cx, cy - size * 0.1, headR, 0, Math.PI * 2);
    ctx.fill();

    // Shoulders — a wide arc below the head
    const shoulderW = size * 0.32;
    const shoulderTop = cy + size * 0.05;
    ctx.beginPath();
    ctx.moveTo(cx - shoulderW, cy + size * 0.3);
    ctx.quadraticCurveTo(cx - shoulderW, shoulderTop, cx, shoulderTop);
    ctx.quadraticCurveTo(cx + shoulderW, shoulderTop, cx + shoulderW, cy + size * 0.3);
    ctx.fill();
  },

  drawContent(ctx, rect, state, dt) {
    const { x, y, w, h } = rect;
    const contacts = getContacts();
    const appState = getState(state);

    // ── Header bar ──────────────────────────────────────────────
    const onlineCount = contacts.filter(c => c.online).length;

    drawText(
      ctx, "Relationships", x + CARD_PAD_X, y + 4,
      w * 0.6, HEADER_FONT, "rgba(255,255,255,0.9)", "left",
    );
    drawText(
      ctx, `${onlineCount} online`, x + w - CARD_PAD_X, y + 5,
      w * 0.3, COUNT_FONT, "#34c759", "right",
    );

    // Thin separator
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    ctx.fillRect(x + CARD_PAD_X, y + HEADER_HEIGHT - 2, w - CARD_PAD_X * 2, 0.5);

    // ── Contact cards ───────────────────────────────────────────
    const listTop = y + HEADER_HEIGHT;
    const listHeight = h - HEADER_HEIGHT;

    ctx.save();
    ctx.beginPath();
    ctx.rect(x, listTop, w, listHeight);
    ctx.clip();

    for (let i = 0; i < contacts.length; i++) {
      const c = contacts[i];
      const cardY = listTop + i * (CARD_HEIGHT + CARD_GAP) - appState.scrollY;

      // Skip cards fully outside the visible area
      if (cardY + CARD_HEIGHT < listTop || cardY > listTop + listHeight) continue;

      const isSelected = appState.selectedIndex === i;
      const bg = isSelected ? CARD_BG_SELECTED : CARD_BG;

      // Card background
      drawRoundRect(ctx, x + 3, cardY, w - 6, CARD_HEIGHT, CARD_RADIUS, bg);

      // ── Avatar circle ─────────────────────────────────────
      const avatarCx = x + 3 + CARD_PAD_X + AVATAR_RADIUS;
      const avatarCy = cardY + CARD_HEIGHT / 2;

      ctx.beginPath();
      ctx.arc(avatarCx, avatarCy, AVATAR_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = c.avatarColor;
      ctx.fill();

      // Initial letter
      ctx.font = "bold 12px sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(c.name[0], avatarCx, avatarCy + 1);

      // ── Online status dot (top-right of avatar) ───────────
      const dotX = avatarCx + AVATAR_RADIUS * 0.7;
      const dotY = avatarCy - AVATAR_RADIUS * 0.7;

      // Dot border (matches card bg for cutout effect)
      ctx.beginPath();
      ctx.arc(dotX, dotY, STATUS_DOT_RADIUS + 1.5, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? CARD_BG_SELECTED : CARD_BG;
      ctx.fill();

      // Dot itself
      ctx.beginPath();
      ctx.arc(dotX, dotY, STATUS_DOT_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = c.online ? "#34c759" : "#636366";
      ctx.fill();

      // ── Text area (to the right of avatar) ────────────────
      const textLeft = avatarCx + AVATAR_RADIUS + 8;
      const textWidth = w - 6 - CARD_PAD_X - (textLeft - x - 3) - CARD_PAD_X;

      // Name
      drawText(ctx, c.name, textLeft, cardY + 6, textWidth * 0.6, NAME_FONT, "#ffffff", "left");

      // Bond tier label (right-aligned)
      const tier = bondTier(c.bond);
      const tColor = tierColor(c.bond);
      drawText(ctx, tier, x + w - 3 - CARD_PAD_X, cardY + 7, textWidth * 0.45, TIER_FONT, tColor, "right");

      // ── Bond progress bar ─────────────────────────────────
      const barY = cardY + 22;
      const barH = 5;
      const barW = textWidth - 24;
      const progress = c.bond / 100;

      // Bar background
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      drawRoundRect(ctx, textLeft, barY, barW, barH, barH / 2);
      ctx.fill();

      // Bar fill — warm gradient
      if (progress > 0) {
        const fillW = Math.max(barH, barW * progress);
        const bColor = bondColor(c.bond);
        const grad = ctx.createLinearGradient(textLeft, 0, textLeft + fillW, 0);
        grad.addColorStop(0, adjustAlpha(bColor, 0.5));
        grad.addColorStop(1, bColor);
        ctx.fillStyle = grad;
        drawRoundRect(ctx, textLeft, barY, fillW, barH, barH / 2);
        ctx.fill();
      }

      // Bond number (right of bar)
      ctx.font = BOND_FONT;
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(`${c.bond}`, textLeft + barW + 4, barY - 1);

      // ── Last seen location ────────────────────────────────
      const locY = cardY + 33;
      drawText(
        ctx, `\u2302 ${c.lastSeen}`, textLeft, locY,
        textWidth, LOCATION_FONT, "rgba(255,255,255,0.35)", "left",
      );
    }

    ctx.restore();

    // ── Empty state (if somehow no contacts) ────────────────
    if (contacts.length === 0) {
      drawText(
        ctx, "No contacts yet", x + w / 2, y + h / 2 - 6,
        w - 20, "9px sans-serif", "rgba(255,255,255,0.3)", "center",
      );
      drawText(
        ctx, "Meet people to add them here", x + w / 2, y + h / 2 + 8,
        w - 20, "7px sans-serif", "rgba(255,255,255,0.2)", "center",
      );
    }

    // ── Momentum / inertia scroll ─────────────────────────────
    const totalHeight = contacts.length * (CARD_HEIGHT + CARD_GAP) - CARD_GAP;
    appState.maxScrollY = Math.max(0, totalHeight - listHeight);

    if (Math.abs(appState.scrollVelocity) > 0.5) {
      appState.scrollY += appState.scrollVelocity * dt;
      appState.scrollVelocity *= Math.exp(-6 * dt);
    } else {
      appState.scrollVelocity = 0;
    }
    appState.scrollY = Math.max(0, Math.min(appState.scrollY, appState.maxScrollY));

    // ── Scroll fade hints ───────────────────────────────────
    if (totalHeight > listHeight) {
      // Bottom fade
      if (appState.scrollY < totalHeight - listHeight - 1) {
        const fadeGrad = ctx.createLinearGradient(0, listTop + listHeight - 16, 0, listTop + listHeight);
        fadeGrad.addColorStop(0, "rgba(0,0,0,0)");
        fadeGrad.addColorStop(1, "rgba(0,0,0,0.4)");
        ctx.fillStyle = fadeGrad;
        ctx.fillRect(x, listTop + listHeight - 16, w, 16);
      }
      // Top fade
      if (appState.scrollY > 1) {
        const fadeGrad = ctx.createLinearGradient(0, listTop, 0, listTop + 16);
        fadeGrad.addColorStop(0, "rgba(0,0,0,0.4)");
        fadeGrad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = fadeGrad;
        ctx.fillRect(x, listTop, w, 16);
      }
    }
  },

  handleTap(localX, localY, state) {
    const contacts = getContacts();
    const appState = getState(state);
    const listTop = HEADER_HEIGHT;

    // Determine which card was tapped
    const adjustedY = localY - listTop + appState.scrollY;
    const cardIndex = Math.floor(adjustedY / (CARD_HEIGHT + CARD_GAP));

    if (cardIndex >= 0 && cardIndex < contacts.length) {
      // Check the tap is within the card (not in the gap)
      const withinCard = adjustedY - cardIndex * (CARD_HEIGHT + CARD_GAP);
      if (withinCard >= 0 && withinCard <= CARD_HEIGHT) {
        // Toggle selection
        appState.selectedIndex = appState.selectedIndex === cardIndex ? -1 : cardIndex;
        return true;
      }
    }

    return false;
  },

  // ── Drag scroll ──────────────────────────────────────────────────────

  handleDrag(dy, state) {
    const appState = getState(state);
    appState.scrollY = Math.max(0, Math.min(appState.scrollY + dy, appState.maxScrollY));
    appState.scrollVelocity = 0;
  },

  handleDragEnd(velocityY, state) {
    const appState = getState(state);
    appState.scrollVelocity = velocityY;
  },

  onOpen(state) {
    const appState = getState(state);
    appState.scrollY = 0;
    appState.scrollVelocity = 0;
    appState.selectedIndex = -1;
  },

  badge(_state) {
    // Show badge for online friends (bond >= 40)
    const onlineFriends = SAMPLE_CONTACTS.filter(c => c.online && c.bond >= 40).length;
    return onlineFriends;
  },
};

// ─── Utilities ───────────────────────────────────────────────────────────

function adjustAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ─── Register ────────────────────────────────────────────────────────────

registerApp(contactsApp);

export { contactsApp };
